import {capitalize} from "@mui/material";
import {useSnackbar} from "notistack";
import {useMutation, useQueryClient} from "react-query";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {ApiError, PhoneNumber, PhoneNumberDaten} from "../../api";
import {useCreatePhoneNumber, useDeletePhoneNumber, useUpdatePhoneNumber} from "../../client/hooks";
import {useUserMandant} from "../useUserMandant";
import {permissions} from "../../permissions";

function useErrorMessage() {
    const {t: tErrors} = useTranslation('errors');
    const {t: tPhonenumbers} = useTranslation('phonenumbers');
    return (error: unknown, fallback: string): string => {
        if (error instanceof ApiError) {
            const body = (error.body ?? {}) as { code?: string; message?: string };
            if (body.code) {
                const [ns, key] = body.code.split(':');
                if (ns === 'phonenumbers' && key) return tPhonenumbers(key);
                if (ns === 'errors' && key) return tErrors(key);
                return body.code;
            }
            if (body.message) return body.message;
        }
        if (error instanceof Error) return error.message;
        return fallback;
    };
}

export function usePhonenumbersHandling() {
    const {t: translation} = useTranslation('phonenumbers');
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const {enqueueSnackbar} = useSnackbar();
    const formatError = useErrorMessage();
    const {isSystemUser, checkHasOneOfPermissions} = useUserMandant();

    const createPhoneNumber = useCreatePhoneNumber();
    const updatePhoneNumber = useUpdatePhoneNumber();
    const deletePhoneNumber = useDeletePhoneNumber();

    const invalidatePhoneNumbers = async () => {
        await queryClient.invalidateQueries(["api", "v1", "phonenumbers"]);
    };

    const createPhoneNumberMutation = useMutation(
        (data: PhoneNumberDaten): Promise<PhoneNumber> => createPhoneNumber(data),
        {
            onSuccess: async (created: PhoneNumber) => {
                await invalidatePhoneNumbers();
                enqueueSnackbar(capitalize(translation('phonenumber-create-successful')), {variant: 'success'});
                navigate(`/phonenumbers/${created.id}`);
            },
            onError: (err) => {
                enqueueSnackbar(formatError(err, capitalize(translation('phonenumber-create-failed'))), {variant: 'error'});
            },
        }
    );

    const updatePhoneNumberMutation = useMutation(
        ({phoneNumberId, data}: {phoneNumberId: string; data: PhoneNumberDaten}): Promise<PhoneNumber> =>
            updatePhoneNumber(phoneNumberId, data),
        {
            onSuccess: async (updated: PhoneNumber) => {
                await invalidatePhoneNumbers();
                await queryClient.invalidateQueries(["api", "v1", "phonenumbers", updated.id]);
                enqueueSnackbar(capitalize(translation('phonenumber-update-successful')), {variant: 'success'});
            },
            onError: (err) => {
                enqueueSnackbar(formatError(err, capitalize(translation('phonenumber-update-failed'))), {variant: 'error'});
            },
        }
    );

    const deletePhoneNumberMutation = useMutation(
        (phoneNumberId: string): Promise<PhoneNumber> => deletePhoneNumber(phoneNumberId),
        {
            onSuccess: async () => {
                await invalidatePhoneNumbers();
                enqueueSnackbar(capitalize(translation('phonenumber-delete-successful')), {variant: 'success'});
            },
            onError: (err) => {
                enqueueSnackbar(formatError(err, capitalize(translation('phonenumber-delete-failed'))), {variant: 'error'});
            },
        }
    );

    const canEditPhoneNumber = (): boolean =>
        isSystemUser || checkHasOneOfPermissions([permissions.phonenumbers.edit]);

    return {
        translation,
        createPhoneNumberMutation,
        updatePhoneNumberMutation,
        deletePhoneNumberMutation,
        canEditPhoneNumber,
    };
}
